<script>
    import { onMount } from 'svelte';
    import { getEndpointId, getPageTitle, initializeRouting } from './utils/routing.js';
    import { initializeChat } from './stores/chatStore.js';
    import ChatInterface from './components/ChatInterface.svelte';
    
    let currentEndpointId = null;
    let showEndpointSelector = false;
    let inputEndpointId = '';
    
    // Initialize the app
    onMount(() => {
        initializeRouting();
        handleRouteChange();
        
        // Listen for route changes
        window.addEventListener('routechange', handleRouteChange);
        
        return () => {
            window.removeEventListener('routechange', handleRouteChange);
        };
    });
    
    // Handle route changes
    function handleRouteChange() {
        const endpointId = getEndpointId();
        currentEndpointId = endpointId;
        showEndpointSelector = !endpointId;
        
        // Update page title
        document.title = getPageTitle(endpointId);
        
        // Initialize chat for the endpoint
        initializeChat(endpointId);
    }
    
    // Navigate to endpoint
    function navigateToEndpoint(endpointId) {
        if (!endpointId) return;
        
        const newUrl = `/${endpointId}`;
        window.history.pushState({}, '', newUrl);
        handleRouteChange();
    }
    
    // Handle endpoint input form
    function handleEndpointSubmit(event) {
        event.preventDefault();
        if (inputEndpointId.trim()) {
            navigateToEndpoint(inputEndpointId.trim());
        }
    }
    
    // Validate UUID format
    function isValidUUID(uuid) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    }
    
    // Example endpoint for demo
    const exampleEndpoint = 'f5d05eb7-812a-4252-a93c-997a46f3cbd1';
    
    function useExampleEndpoint() {
        navigateToEndpoint(exampleEndpoint);
    }
</script>

<main id="app">
    {#if showEndpointSelector}
        <div class="endpoint-selector">
            <div class="selector-container">
                <div class="selector-header">
                    <h1 class="selector-title">Chat Interface</h1>
                    <p class="selector-description">
                        Geben Sie eine Endpoint-ID ein, um mit dem entsprechenden n8n-Workflow zu chatten.
                    </p>
                </div>
                
                <form class="endpoint-form" on:submit={handleEndpointSubmit}>
                    <div class="input-group">
                        <label for="endpoint-input" class="input-label">
                            Endpoint ID
                        </label>
                        <input
                            id="endpoint-input"
                            type="text"
                            bind:value={inputEndpointId}
                            placeholder="z.B. f5d05eb7-812a-4252-a93c-997a46f3cbd1"
                            class="endpoint-input"
                            class:invalid={inputEndpointId && !isValidUUID(inputEndpointId)}
                        />
                        {#if inputEndpointId && !isValidUUID(inputEndpointId)}
                            <div class="input-error">
                                Bitte geben Sie eine gültige UUID ein
                            </div>
                        {/if}
                    </div>
                    
                    <button 
                        type="submit" 
                        class="submit-button"
                        disabled={!inputEndpointId.trim() || !isValidUUID(inputEndpointId)}
                    >
                        Chat starten
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12,5 19,12 12,19"></polyline>
                        </svg>
                    </button>
                </form>
                
                <div class="example-section">
                    <p class="example-text">Oder verwenden Sie das Beispiel:</p>
                    <button 
                        type="button" 
                        class="example-button"
                        on:click={useExampleEndpoint}
                    >
                        Demo-Endpoint verwenden
                    </button>
                </div>
                
                <div class="info-section">
                    <h3 class="info-title">Wie funktioniert es?</h3>
                    <ul class="info-list">
                        <li>Jede Endpoint-ID entspricht einem n8n-Workflow</li>
                        <li>Die URL wird zu: <code>chat.prozesshub.eu/[endpoint-id]</code></li>
                        <li>Chat-Verlauf wird lokal im Browser gespeichert</li>
                        <li>JSON-Blöcke aus KI-Antworten werden separat archiviert</li>
                    </ul>
                </div>
            </div>
        </div>
    {:else}
        <ChatInterface endpointId={currentEndpointId} />
    {/if}
</main>

<style>
    .endpoint-selector {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #1a1a1a;
        padding: 20px;
    }
    
    .selector-container {
        max-width: 600px;
        width: 100%;
        background: #2d2d2d;
        border-radius: 12px;
        padding: 40px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }
    
    .selector-header {
        text-align: center;
        margin-bottom: 32px;
    }
    
    .selector-title {
        font-size: 2rem;
        font-weight: 700;
        margin: 0 0 16px 0;
        color: #ffffff;
        background: linear-gradient(135deg, #4a9eff 0%, #0066cc 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
    
    .selector-description {
        font-size: 1.1rem;
        color: #ccc;
        margin: 0;
        line-height: 1.6;
    }
    
    .endpoint-form {
        margin-bottom: 32px;
    }
    
    .input-group {
        margin-bottom: 24px;
    }
    
    .input-label {
        display: block;
        font-size: 0.9rem;
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .endpoint-input {
        width: 100%;
        padding: 16px 20px;
        background: #1a1a1a;
        border: 2px solid #404040;
        border-radius: 8px;
        color: #ffffff;
        font-size: 1rem;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        transition: all 0.2s ease;
    }
    
    .endpoint-input:focus {
        outline: none;
        border-color: #4a9eff;
        box-shadow: 0 0 0 3px rgba(74, 158, 255, 0.1);
    }
    
    .endpoint-input.invalid {
        border-color: #dc3545;
    }
    
    .endpoint-input::placeholder {
        color: #666;
        font-size: 0.9rem;
    }
    
    .input-error {
        margin-top: 8px;
        font-size: 0.85rem;
        color: #dc3545;
    }
    
    .submit-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        padding: 16px 24px;
        background: linear-gradient(135deg, #4a9eff 0%, #0066cc 100%);
        border: none;
        border-radius: 8px;
        color: white;
        font-size: 1.1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .submit-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(74, 158, 255, 0.3);
    }
    
    .submit-button:active:not(:disabled) {
        transform: translateY(0);
    }
    
    .submit-button:disabled {
        background: #555;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
    
    .example-section {
        text-align: center;
        margin-bottom: 32px;
        padding: 24px 0;
        border-top: 1px solid #404040;
        border-bottom: 1px solid #404040;
    }
    
    .example-text {
        margin: 0 0 16px 0;
        color: #888;
        font-size: 0.9rem;
    }
    
    .example-button {
        background: transparent;
        border: 2px solid #4a9eff;
        border-radius: 6px;
        color: #4a9eff;
        padding: 12px 20px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .example-button:hover {
        background: #4a9eff;
        color: white;
    }
    
    .info-section {
        color: #ccc;
    }
    
    .info-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 0 16px 0;
        color: #ffffff;
    }
    
    .info-list {
        margin: 0;
        padding-left: 20px;
        line-height: 1.8;
    }
    
    .info-list li {
        margin-bottom: 8px;
    }
    
    .info-list code {
        background: #404040;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 0.85em;
        color: #4a9eff;
    }
    
    @media (max-width: 768px) {
        .endpoint-selector {
            padding: 16px;
        }
        
        .selector-container {
            padding: 24px;
        }
        
        .selector-title {
            font-size: 1.5rem;
        }
        
        .selector-description {
            font-size: 1rem;
        }
        
        .endpoint-input {
            padding: 14px 16px;
            font-size: 16px; /* Prevents zoom on iOS */
        }
        
        .submit-button {
            padding: 14px 20px;
            font-size: 1rem;
        }
    }
</style>
